import React, { useMemo, useState, useEffect, useCallback } from "react";
import { searchApi, useAppSelector } from "@litres-core/store";
import { isThisYear, parseISO } from "@litres/utils/date-fns";
import { useLocation, EPlatform } from "@litres-libs/global-hooks";
import {
  deleteCookie,
  getCookie,
  getFromLocalStorage,
  hasWindow,
  removeFromLocalStorage,
  setCookie,
} from "@litres/utils";
import { useDebounce } from "usehooks-ts";
import type {
  ILastSearch,
  ISearchHistoryItem,
  ISearchResultItem,
} from "./types";
import { DesktopSuggestionsSearch } from "./DesktopSuggestionsSearch/DesktopSuggestionsSearch";
import { MobileSuggestionsSearch } from "./MobileSuggestionsSearch/MobileSuggestionsSearch";
import {
  getEditDistance,
  getLastSearchResults,
  getSuggestionSearchResults,
  longestCommonSubstring,
  saveLastSearchResultsToLocalStorage,
} from "./utils";
import { useApi } from "../../hooks/useApi";
import { trackSearchHistoryItem, trackSearchSuggestionItem } from "./analytics";

interface SuggestionsSearchProps {
  platform: EPlatform;
}

// TODO: utils / explain regex
const checkSearchValue = (value: string): boolean => {
  /* eslint-disable no-useless-escape */
  return (
    (value &&
      value.replace(/[\s\\\/~#$%^&*(),._!@"№;:?=«»'`\[\]{}+\-<>]/g, "").length >
        0) ||
    /^\d+$/.test(value)
  );
};

/**
 * Проверка, нужно ли обновить результаты последнего поиска.
 * Если с последней проверки прошло больше 20 минут - обновляем.
 */
const needToUpdateLastSearch = (): boolean => {
  const lastSearchDate = getCookie("last_search");
  if (lastSearchDate) {
    const minutesDiff = (Number(new Date()) - Number(lastSearchDate)) / 60000;
    if (minutesDiff < 20) {
      return false;
    }
  }
  return true;
};

const setLastSearchCookie = (): void => {
  setCookie("last_search", String(+new Date()), {
    expires: 1800,
  });
};

// TODO: utils
const lastSearchAdapter = (search: ISearchHistoryItem[]): ILastSearch[] => {
  return search.map(item => {
    return {
      value: item.s_search,
      source: "server",
    } as ILastSearch;
  });
};

/**
 * Отдаст индекс объекта из истории поиска, если такой имеется (иначе -1)
 */
const getIndexOfLastSearchItem = (
  val: string,
  lastSearch: ILastSearch[]
): number => {
  return lastSearch.map(obj => obj.value).indexOf(val);
};

// TODO: utils
const containsRepeatWords = (
  searchPhrase: string,
  lastSearch: string
): boolean => {
  return (
    searchPhrase === lastSearch ||
    (getEditDistance(searchPhrase, lastSearch) <= 5 &&
      longestCommonSubstring(searchPhrase, lastSearch) >= 3)
  );
};

export const SuggestionsSearch = ({
  platform = EPlatform.desktop,
}: SuggestionsSearchProps) => {
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search?.substr(1));
  const [_isLoading, setLoading] = useState<boolean>(false);
  const [isHistoryRefreshed, setHistoryRefreshed] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const debouncedSearchValue = useDebounce(searchValue, 400);

  const [getSuggestions, { data: suggestions }] =
    searchApi.useLazyGetSuggestionsQuery();

  const [lastSearch, setLastSearch] = useState<ILastSearch[]>(
    getLastSearchResults()
  );
  const { getSearchHistory, saveSearchQuery, refreshSearchResults } = useApi();

  const { query: searchQuery, appliedCorrection } = useAppSelector(
    state => state.global.searchPage
  );

  const { history = [] } = useAppSelector(state => state.global.search);

  const isUserAnonymous = useAppSelector(
    state => state.foundationUser.id === 0
  );

  const [isUserHasHistory, setUserHasHistory] = useState<boolean>(
    !!(hasWindow && getFromLocalStorage("last_search"))
  );

  const searchResultItems: ISearchResultItem[] = useMemo(
    () =>
      getSuggestionSearchResults(searchValue, lastSearch, suggestions || []),
    [searchValue, lastSearch, suggestions]
  );

  // TODO: refactor
  const injectDBHistoryIntoStorage = (): void => {
    const lastYearRequests = history!.filter(
      (historyItem: ISearchHistoryItem) => {
        const { time } = historyItem;

        const parsedDate = parseISO(time);

        return isThisYear(parsedDate);
      }
    );

    // Возможно стоит отрезать лишнее внутри компонента истории
    const lastNineRequests = lastYearRequests.filter(
      (historyItem: ISearchHistoryItem, index: number) => index < 9
    );

    setLastSearch(lastSearchAdapter(lastNineRequests));
  };

  const refreshHistory = (): void => {
    if (hasWindow) {
      removeFromLocalStorage("last_search");
      setLastSearch([]);
      deleteCookie("last_search");
      setUserHasHistory(false);
    }
  };

  const handleDeleteSearchItemClick = (value: string) => {
    setLastSearch(currentLastSearchValue => {
      const lastSearchResults = currentLastSearchValue.slice();
      return lastSearchResults.filter(searchItem => searchItem.value !== value);
    });
  };

  const handleSearchItemClick = useCallback(
    (item: ISearchResultItem, itemIndex?: number) => {
      if (item.type === "suggestion") {
        trackSearchSuggestionItem(item, {
          query: searchValue,
          rowNumber: typeof itemIndex === "number" ? itemIndex + 1 : 0,
        });
        addItemToLastSearchStorage(item.value);
      }

      if (item.type === "history") {
        trackSearchHistoryItem(item);
      }

      setSearchValue(item.value);
    },
    [searchValue]
  );

  const handleClearSearch = () => {
    setSearchValue("");
  };

  const addItemToLastSearchStorage = (
    newValue: string,
    position?: number
  ): void => {
    setLastSearchCookie();
    // повторно записи не добавляем

    if (getIndexOfLastSearchItem(newValue, lastSearch) !== -1) {
      return;
    }

    const newItem: ILastSearch = {
      value: newValue,
      source: "client",
      timestamp: Date.now(),
    };

    setLastSearch(currentLastSearchValue => {
      const lastSearchResults = currentLastSearchValue.slice();
      if (position || position === 0) {
        lastSearchResults[position] = newItem;
      } else {
        lastSearchResults.push(newItem);
      }
      if (lastSearchResults.length > 5) {
        lastSearchResults.shift();
      }
      return lastSearchResults;
    });
  };

  useEffect(() => {
    // TODO: бэк обрабатывает регистрозависимо (есть задача на фикс)
    const value = debouncedSearchValue.toLowerCase();
    if (checkSearchValue(value)) {
      getSuggestions({ q: value });
      setHistoryRefreshed(false);

      for (let i = 0; i < lastSearch.length; i++) {
        if (containsRepeatWords(value, lastSearch[i].value)) {
          addItemToLastSearchStorage(value, i);
          return;
        }
      }
      if (value.length > 2) {
        addItemToLastSearchStorage(value);
      }
    }
  }, [debouncedSearchValue]);

  useEffect(() => {
    if (searchValue.length === 0) {
      getSuggestions({});
    }
  }, [searchValue]);

  const handleOnChange = (value: string) => {
    setSearchValue(value);
  };

  /**
   * Изменит source саджеста из истории поиска с "client" на "server"
   */
  const setLastSearchResultToServer = (val: string): void => {
    const valIndex = getIndexOfLastSearchItem(val, lastSearch);

    // если такого индекса не нашлось — тихо завершаем работу
    if (valIndex === -1) {
      return;
    }

    setLastSearch(currentLastSearchValue => {
      const lastSearchResults = currentLastSearchValue.slice();
      lastSearchResults[valIndex].source = "server";
      return lastSearchResults;
    });
  };

  const handleSearch = (q: string) => {
    if (!checkSearchValue(q)) {
      return;
    }

    if (!isUserAnonymous) {
      saveSearchQuery(searchValue);
      setLastSearchResultToServer(searchValue);
    }

    setLoading(true);

    // Добавляем юзеру куку, чтобы он перешел на новый поиск, а не старый
    setCookie("next_search", "1", {
      expires: 864000, // 10 дней
    });

    window.location.assign(`/search/?q=${encodeURIComponent(q)}`);

    if (!isUserAnonymous) {
      saveSearchQuery(q);
      setLastSearchResultToServer(q);
    }

    return;
  };

  useEffect(() => {
    if (!checkSearchValue(searchValue)) {
      refreshSearchResults();
    }
  }, [searchValue]);

  useEffect(() => {
    setSearchValue(
      appliedCorrection ? appliedCorrection.to_query : searchQuery
    );
  }, [searchQuery, appliedCorrection]);

  useEffect(() => {
    setSearchValue(searchParams.get("q") || "");

    if (isUserAnonymous || (isUserHasHistory && !needToUpdateLastSearch())) {
      return;
    }

    getSearchHistory([0, 20]);
  }, []);

  useEffect(() => {
    if (history.length === 0) {
      return;
    }

    injectDBHistoryIntoStorage();
  }, [history]);

  useEffect(() => {
    saveLastSearchResultsToLocalStorage(lastSearch);
    setLastSearchCookie();
    if (lastSearch.length > 0) {
      setUserHasHistory(true);
    }
  }, [lastSearch]);

  useEffect(() => {
    if (isHistoryRefreshed) {
      refreshHistory();
    }
  }, [isHistoryRefreshed]);

  if (platform === EPlatform.mobile) {
    return (
      <MobileSuggestionsSearch
        searchValue={searchValue}
        searchResultItems={searchResultItems}
        onSearchChange={handleOnChange}
        onItemClick={handleSearchItemClick}
        onItemDeleteClick={handleDeleteSearchItemClick}
        onSearch={handleSearch}
        onClearClick={handleClearSearch}
      />
    );
  }

  return (
    <DesktopSuggestionsSearch
      searchValue={searchValue}
      searchResultItems={searchResultItems}
      onSearchChange={handleOnChange}
      onItemClick={handleSearchItemClick}
      onItemDeleteClick={handleDeleteSearchItemClick}
      onSearch={handleSearch}
      onClearClick={handleClearSearch}
    />
  );
};
